import React, { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

const EstadisticasEvaluaciones = ({ userData, tipo = "rrhh" }) => {
  const [estadisticas, setEstadisticas] = useState({
    evaluacionesPendientes: 0,
    evaluacionesCompletadas: 0,
    evaluacionesVencidas: 0,
    promedioCompletitud: 0,
    totalEmpleados: 0,
    evaluacionesEnProceso: 0,
    totalEvaluaciones: 0,
    tasaRiesgo: 0,
    tasaProgreso: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userData) {
      loadEstadisticas();
    }
  }, [userData]);

  const loadEstadisticas = async () => {
    try {
      // Obtener empresa ID de manera flexible
      const empresaId = userData.empresaId || userData.empresa_id || userData.empresa;
      
      // Query base dependiendo del tipo de usuario
      let baseQuery;
      if (userData.rol === 'superadmin') {
        // SuperAdmin ve todas las evaluaciones
        baseQuery = collection(db, "evaluaciones_asignadas");
      } else if (empresaId) {
        // RRHH y Psicólogo ven solo de su empresa
        baseQuery = query(
          collection(db, "evaluaciones_asignadas"),
          where("empresa_id", "==", empresaId)
        );
      } else {
        // Sin empresa definida, no cargar datos
        console.warn("No se encontró empresaId para el usuario");
        setLoading(false);
        return;
      }

      const evaluacionesSnapshot = await getDocs(baseQuery);
      const evaluaciones = evaluacionesSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));

      // Calcular estadísticas
      const stats = calcularEstadisticas(evaluaciones);
      
      // Cargar empleados si es necesario
      if (tipo === "rrhh") {
        const empleadosCount = await contarEmpleados();
        stats.totalEmpleados = empleadosCount;
      }

      setEstadisticas(stats);
    } catch (error) {
      console.error("Error al cargar estadísticas:", error);
    } finally {
      setLoading(false);
    }
  };

  const calcularEstadisticas = (evaluaciones) => {
    const ahora = new Date();
    
    // Filtrar evaluaciones activas
    const evaluacionesActivas = evaluaciones.filter(e => e.activa === true);
    const totalActivas = evaluacionesActivas.length;
    
    const pendientes = evaluacionesActivas.filter(e => 
      e.estado === 'pendiente'
    ).length;

    const completadas = evaluaciones.filter(e => 
      e.estado === 'completada'
    ).length;

    const enProceso = evaluacionesActivas.filter(e => 
      e.estado === 'en_proceso'
    ).length;

    // Mejorar cálculo de vencidas - considerar diferentes campos de fecha
    const vencidas = evaluacionesActivas.filter(e => {
      if (e.estado === 'completada') return false;
      
      let fechaFin = null;
      if (e.fechaFin && e.fechaFin.seconds) {
        fechaFin = new Date(e.fechaFin.seconds * 1000);
      } else if (e.fecha_fin && e.fecha_fin.seconds) {
        fechaFin = new Date(e.fecha_fin.seconds * 1000);
      } else if (e.fechaCierre) {
        fechaFin = new Date(e.fechaCierre);
      } else if (e.fecha_cierre) {
        fechaFin = new Date(e.fecha_cierre);
      }
      
      return fechaFin && fechaFin < ahora;
    }).length;

    // Cálculo mejorado del promedio de completitud
    // Incluye evaluaciones completadas vs total de evaluaciones (activas + completadas)
    const totalEvaluaciones = totalActivas + completadas;
    const promedioCompletitud = totalEvaluaciones > 0 
      ? Math.round((completadas / totalEvaluaciones) * 100) 
      : 0;

    // Métricas adicionales para el análisis
    const tasaRiesgo = totalActivas > 0 
      ? Math.round((vencidas / totalActivas) * 100)
      : 0;

    const tasaProgreso = totalActivas > 0 
      ? Math.round((enProceso / totalActivas) * 100)
      : 0;

    return {
      evaluacionesPendientes: pendientes,
      evaluacionesCompletadas: completadas,
      evaluacionesVencidas: vencidas,
      evaluacionesEnProceso: enProceso,
      promedioCompletitud,
      totalActivas,
      totalEvaluaciones,
      tasaRiesgo,
      tasaProgreso
    };
  };

  const contarEmpleados = async () => {
    try {
      let empleadosQuery;
      if (userData.rol === 'superadmin') {
        empleadosQuery = collection(db, "empleados");
      } else {
        empleadosQuery = query(
          collection(db, "empleados"),
          where("empresa_id", "==", userData.empresaId)
        );
      }
      
      const empleadosSnapshot = await getDocs(empleadosQuery);
      return empleadosSnapshot.docs.length;
    } catch (error) {
      console.error("Error al contar empleados:", error);
      return 0;
    }
  };

  // Configuración específica por tipo de usuario
  const getConfiguracion = () => {
    if (tipo === "psicologo") {
      return {
        titulo: "Análisis Psicológico - Dashboard Integral",
        tarjetas: [
          {
            id: "promedio",
            valor: `${estadisticas.promedioCompletitud}%`,
            titulo: "Promedio General de Completitud",
            descripcion: `${estadisticas.evaluacionesCompletadas} de ${estadisticas.totalEvaluaciones} evaluaciones`,
            color: "#7c3aed",
            fondo: "#f3e8ff",
            icono: "📊"
          },
          {
            id: "completadas",
            valor: estadisticas.evaluacionesCompletadas,
            titulo: "Evaluaciones Completadas",
            descripcion: "Listas para análisis psicológico",
            color: "#16a34a",
            fondo: "#dcfce7",
            icono: "✅"
          },
          {
            id: "pendientes",
            valor: estadisticas.evaluacionesPendientes,
            titulo: "Pendientes de Análisis",
            descripcion: "Requieren seguimiento",
            color: "#f59e0b",
            fondo: "#fef3c7",
            icono: "🧠"
          },
          {
            id: "vencidas",
            valor: estadisticas.evaluacionesVencidas,
            titulo: "Requieren Atención Urgente",
            descripcion: `${estadisticas.tasaRiesgo}% de riesgo`,
            color: "#dc2626",
            fondo: "#fef2f2",
            icono: "🚨"
          }
        ]
      };
    } else {
      // Configuración para RRHH - también incluir promedio de completitud
      return {
        titulo: "Gestión de Evaluaciones",
        tarjetas: [
          {
            id: "promedio",
            valor: `${estadisticas.promedioCompletitud}%`,
            titulo: "Promedio de Completitud",
            descripcion: `${estadisticas.evaluacionesCompletadas} completadas`,
            color: "#8b5cf6",
            fondo: "#f3e8ff",
            icono: "📊"
          },
          {
            id: "completadas",
            valor: estadisticas.evaluacionesCompletadas,
            titulo: "Completadas",
            descripcion: "Finalizadas exitosamente",
            color: "#10b981",
            fondo: "#d1fae5",
            icono: "✅"
          },
          {
            id: "pendientes",
            valor: estadisticas.evaluacionesPendientes,
            titulo: "Pendientes",
            descripcion: "Por completar",
            color: "#f59e0b",
            fondo: "#fef3c7",
            icono: "⏳"
          },
          {
            id: "vencidas",
            valor: estadisticas.evaluacionesVencidas,
            titulo: "Vencidas",
            descripcion: "Requieren seguimiento",
            color: "#ef4444",
            fondo: "#fee2e2",
            icono: "🚨"
          }
        ]
      };
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{
            backgroundColor: '#f8fafc',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            textAlign: 'center'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              backgroundColor: '#e2e8f0',
              borderRadius: '50%',
              margin: '0 auto 0.5rem',
              animation: 'pulse 2s infinite'
            }}></div>
            <div style={{
              height: '20px',
              backgroundColor: '#e2e8f0',
              borderRadius: '4px',
              marginBottom: '0.5rem'
            }}></div>
            <div style={{
              height: '14px',
              backgroundColor: '#f1f5f9',
              borderRadius: '4px'
            }}></div>
          </div>
        ))}
      </div>
    );
  }

  const configuracion = getConfiguracion();

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2 style={{ 
        fontSize: '1.25rem', 
        fontWeight: '600', 
        color: '#1e293b',
        marginBottom: '1rem'
      }}>
        📊 {configuracion.titulo}
      </h2>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem'
      }}>
        {configuracion.tarjetas.map((tarjeta) => (
          <div key={tarjeta.id} style={{
            backgroundColor: tarjeta.fondo,
            padding: '1.5rem',
            borderRadius: '8px',
            textAlign: 'center',
            border: `1px solid ${tarjeta.color}20`,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'pointer'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
          >
            <div style={{ 
              fontSize: '2rem', 
              marginBottom: '0.5rem' 
            }}>
              {tarjeta.icono}
            </div>
            <div style={{ 
              fontSize: '2.5rem', 
              color: tarjeta.color, 
              fontWeight: 'bold',
              marginBottom: '0.5rem' 
            }}>
              {tarjeta.valor}
            </div>
            <h3 style={{ 
              margin: '0 0 0.5rem 0', 
              color: tarjeta.color,
              fontSize: '1rem',
              fontWeight: '600'
            }}>
              {tarjeta.titulo}
            </h3>
            <p style={{ 
              margin: 0, 
              color: tarjeta.color, 
              fontSize: '0.875rem',
              opacity: 0.8
            }}>
              {tarjeta.descripcion}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EstadisticasEvaluaciones;
